import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import useAuth from "../hooks/useAuth";
import UseAxiosPrivate from "../hooks/useAxiosPrivate";
import { useSIP } from "./SIPContext";
import { FOLLOWUP_REPORT_URL } from "../utils/ApiUrls";

const FOLLOWUP_CHECK_INTERVAL_MS = 1000;
const FOLLOWUP_FETCH_INTERVAL_MS = 30000;
const DEFAULT_NOTIFY_LEAD_MS = 5000;
// Gap enforced between one auto-dialed follow-up ending and the next queued
// one starting, so back-to-back follow-ups due at the same time don't dial
// out the instant the previous call ends.
const POST_CALL_GAP_MS = 10000;
// Fixed cutoff independent of the agent's configurable notify-lead setting -
// once a follow-up is this far past its due time, it's considered breached
// and must never be queued/auto-dialed (e.g. after a refresh brings it back
// into memory with a clean notifiedRef).
const MAX_OVERDUE_MS = 60000;
const FOLLOWUP_NOTIFY_PREF_KEY = "followupNotifyEnabled";
const FOLLOWUP_NOTIFY_LEAD_SEC_KEY = "followupNotifyLeadSec";
export const MIN_NOTIFY_LEAD_SEC = 1;

const FollowupNotificationContext = createContext(null);

const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

// Mounted once above the app's <Outlet> (see PersistLogin.js), alongside
// WebRtcIncomingCallHandler, so a due follow-up auto-dials regardless of
// which page the agent is currently on instead of only while Dashboard2 (the
// component that used to own this polling/dialing logic) happens to be mounted.
export const FollowupNotificationProvider = ({ children, onlineStatus }) => {
  const { auth } = useAuth();
  const axiosPrivate = UseAxiosPrivate("followup-notifier");
  const { makeCall, callStatus } = useSIP();
  const isAdmin = auth?.roles?.includes("sys_admin");
  const hasWebRtc =
    (auth?.permissions?.data || []).some((p) => p.name === "WebRtc") ||
    (isAdmin && !!auth?.user_details?.sip_details);
  const isOnCall = callStatus !== "idle";
  const userId = auth?.user_details?.user;
  // Auto-dialing should only happen while the agent is actually available -
  // not on a break/unavailable, where they shouldn't be placed on outbound
  // calls without choosing to.
  const isAvailable = onlineStatus === "available";

  const [notifyEnabled, setNotifyEnabled] = useState(
    () => localStorage.getItem(FOLLOWUP_NOTIFY_PREF_KEY) !== "false"
  );
  const [notifyLeadSec, setNotifyLeadSec] = useState(() => {
    const stored = parseInt(localStorage.getItem(FOLLOWUP_NOTIFY_LEAD_SEC_KEY), 10);
    if (Number.isFinite(stored) && stored >= MIN_NOTIFY_LEAD_SEC) {
      return stored;
    }
    return Math.round(DEFAULT_NOTIFY_LEAD_MS / 1000);
  });

  const toggleNotify = useCallback(() => {
    setNotifyEnabled((prev) => {
      const next = !prev;
      localStorage.setItem(FOLLOWUP_NOTIFY_PREF_KEY, String(next));
      return next;
    });
  }, []);

  const setNotifyLeadSeconds = useCallback((raw) => {
    if (!Number.isFinite(raw)) return;
    const clamped = Math.max(MIN_NOTIFY_LEAD_SEC, raw);
    setNotifyLeadSec(clamped);
    localStorage.setItem(FOLLOWUP_NOTIFY_LEAD_SEC_KEY, String(clamped));
  }, []);

  const effectiveNotifyLeadMs = notifyLeadSec * 1000;

  // Always "today", independent of whatever date range Dashboard2's own
  // calendar filter happens to be browsing.
  const [followUps, setFollowUps] = useState([]);

  const fetchFollowUps = useCallback(() => {
    if (!userId) return Promise.resolve();
    const today = todayISO();
    return axiosPrivate
      .post(FOLLOWUP_REPORT_URL, {
        user_id: userId,
        from_date: today,
        to_date: today,
        entry_cap: "yes",
        userType: "sys_user",
      })
      .then((res) => setFollowUps(Array.isArray(res.data) ? res.data : res.data?.results || []))
      .catch((err) => console.log(err));
  }, [axiosPrivate, userId]);

  useEffect(() => {
    if (!notifyEnabled || !userId || !isAvailable) return undefined;
    fetchFollowUps();
    const id = setInterval(fetchFollowUps, FOLLOWUP_FETCH_INTERVAL_MS);
    return () => clearInterval(id);
  }, [notifyEnabled, userId, isAvailable, fetchFollowUps]);

  const notifiedRef = useRef(new Set());
  const cancelledRef = useRef(new Set());
  const [pendingCalls, setPendingCalls] = useState([]);
  const [nowTick, setNowTick] = useState(Date.now());

  // Tracks when the agent's last call ended, so back-to-back queued
  // follow-ups wait out POST_CALL_GAP_MS instead of dialing the instant
  // isOnCall flips back to false.
  const lastCallEndAtRef = useRef(0);
  const wasOnCallRef = useRef(isOnCall);
  useEffect(() => {
    if (wasOnCallRef.current && !isOnCall) {
      lastCallEndAtRef.current = Date.now();
    }
    wasOnCallRef.current = isOnCall;
  }, [isOnCall]);

  useEffect(() => {
    if (!notifyEnabled || !isAvailable) return undefined;

    const checkUpcomingFollowUps = () => {
      const now = Date.now();

      // Queue a countdown notification once a follow-up enters its lead
      // window, but skip it once it's gone well past due (e.g. tab was
      // backgrounded).
      followUps.forEach((f) => {
        if (!f.follow_up_date) return;
        const dueAt = new Date(f.follow_up_date).getTime();
        if (Number.isNaN(dueAt)) return;

        const msRemaining = dueAt - now;
        if (msRemaining > effectiveNotifyLeadMs || msRemaining < -MAX_OVERDUE_MS) return;

        const key = `${f.customer_number}-${f.follow_up_date}`;
        if (notifiedRef.current.has(key)) return;
        notifiedRef.current.add(key);

        setPendingCalls((prev) => [
          ...prev,
          {
            key,
            customer_name: f.customer_name,
            customer_number: f.customer_number,
            event_id: f.event_id,
            dueAt,
          },
        ]);
      });

      // Once a queued item's countdown reaches zero it becomes "queued" for
      // dialing. If the agent is already on a call, it waits there instead
      // of interrupting the active session; as soon as the line is free the
      // oldest queued item (that wasn't cancelled) is auto-dialed via WebRTC.
      setPendingCalls((prev) => {
        const dueNow = Date.now();
        let dialedThisTick = false;
        return prev
          .map((item) => (item.queued || dueNow < item.dueAt ? item : { ...item, queued: true }))
          .filter((item) => {
            if (!item.queued) return true;

            if (cancelledRef.current.has(item.key)) {
              cancelledRef.current.delete(item.key);
              return false;
            }

            if (dueNow - item.dueAt > MAX_OVERDUE_MS) return false;

            if (isOnCall || dialedThisTick) return true;

            const sinceLastCallEnded = Date.now() - lastCallEndAtRef.current;
            if (lastCallEndAtRef.current && sinceLastCallEnded < POST_CALL_GAP_MS) return true;

            if (hasWebRtc && typeof makeCall === "function") {
              makeCall(item.customer_number, item.event_id);
              dialedThisTick = true;
            }
            return false;
          });
      });
    };

    checkUpcomingFollowUps();
    const intervalId = setInterval(checkUpcomingFollowUps, FOLLOWUP_CHECK_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [followUps, effectiveNotifyLeadMs, notifyEnabled, isAvailable, hasWebRtc, makeCall, isOnCall]);

  useEffect(() => {
    if (pendingCalls.length === 0) return undefined;
    const tickId = setInterval(() => setNowTick(Date.now()), 250);
    return () => clearInterval(tickId);
  }, [pendingCalls.length]);

  const cancelPendingCall = useCallback((key) => {
    cancelledRef.current.add(key);
    setPendingCalls((prev) => prev.filter((item) => item.key !== key));
  }, []);

  // Lets the popup's "Call now" button skip the rest of the countdown
  // instead of waiting it out.
  const callPendingCallNow = useCallback(
    (key) => {
      if (isOnCall || !hasWebRtc || typeof makeCall !== "function") return;
      setPendingCalls((prev) => {
        const item = prev.find((p) => p.key === key);
        if (!item) return prev;
        makeCall(item.customer_number, item.event_id);
        return prev.filter((p) => p.key !== key);
      });
    },
    [isOnCall, hasWebRtc, makeCall]
  );

  const value = {
    notifyEnabled,
    toggleNotify,
    notifyLeadSec,
    setNotifyLeadSeconds,
    pendingCalls,
    nowTick,
    cancelPendingCall,
    callPendingCallNow,
    hasWebRtc,
    isOnCall,
    followUps,
    refetchFollowUps: fetchFollowUps,
  };

  return (
    <FollowupNotificationContext.Provider value={value}>
      {children}
    </FollowupNotificationContext.Provider>
  );
};

export const useFollowupNotification = () => {
  const context = useContext(FollowupNotificationContext);
  if (!context) throw new Error("useFollowupNotification must be used within FollowupNotificationProvider");
  return context;
};
